<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onBeforeMount } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import * as yup from "yup"

import { useSourceInfo, type OwnerOutput } from "@core/composables"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useConfigStore } from "@core/stores/config.store"
import { type SensorModel } from "@core/machines"
import type {
    SourceBrand,
    SourceLegacyChannel,
    SourceLegacyOwnerCompany,
    SourceModel,
} from "@types"
import SensorFormChannels, {
    type SourceLegacyChannelWithDisabled,
} from "@core/components/source/sensor/form/channels/SensorFormChannels.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            form: {
                title: "Informations du capteur",
                operator_label: "Opérateur",
                idsource_label: "Identifiant du capteur",
                brand_label: "Marque",
                model_label: "Modèle",
                brand_auto_detected:
                    "La marque a été détectée automatiquement grâce à l'identifiant du capteur.",
                not_found_model: "Je ne trouve pas mon capteur",
                channels_label: "Canaux",
                name_label: "Nom du capteur",
                name_hint:
                    "Optionnel - Donnez un nom personnalisé à votre capteur",
                owner_label: "Propriétaire",
                submit: "Valider",
                cancel: "Annuler",
                back: "Retour",
                change_operator: "Modifier l'opérateur",
                keepOwner: "Conserver le titulaire",
                changeOwner: "Modifier le titulaire",
                currentOwner: "Titulaire actuel",
                currentOwnerWarning:
                    "Le titulaire actuel est un particulier. Veuillez modifier le titulaire.",
                validation: {
                    idsource_required:
                        "L'identifiant du capteur est obligatoire.",
                    operator_required: "L'opérateur est obligatoire.",
                    brand_required: "La marque est obligatoire.",
                    model_required: "Le modèle est obligatoire.",
                },
                warningParticulierNotAllowed:
                    "Le titulaire doit être une personne morale (professionnel) pour un capteur. Veuillez renseigner quelqu'un d'autre.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

interface InformationFormValue {
    idsource: string
    operator: string
    brand: string
    model: string
    channels: SourceLegacyChannel[]
    name: string
    owner?: OwnerOutput | null
}

interface Props {
    /**
     * Valeurs initiales du formulaire
     */
    value?: {
        idsource?: string
        operator?: string
        brand?: string
        model?: string
        channels?: SourceLegacyChannel[]
        name?: string
        owner?: OwnerOutput
    }
    /**
     * Liste des modèles candidats détectés par la recherche réseau.
     * Le shape réel envoyé par `data` (task `source.find_config.request`) est
     * `{ sourcebrand: string; model: string }[]` — voir
     * `SensorFormSearch.vue#SearchResult`. On utilise le type loose
     * `SensorModel` (cf. `sensor-form.machine.ts`) qui accepte les 2 shapes
     * historiques (`{idname, name}` des stories/tests + `{sourcebrand, model}`
     * de la prod).
     */
    models?: SensorModel[]
    /**
     * Indique si on est en mode édition
     */
    isEditing?: boolean
    /**
     * Cache le bouton d'annulation
     */
    hideCancel?: boolean
    /**
     * Indique si le propriétaire est requis
     */
    requiredOwner?: boolean
    /**
     * Informations sur le propriétaire actuel
     */
    currentOwner?: SourceLegacyOwnerCompany | null
    /**
     * Entrée directe sans sélecteur : Retour titulaire désactivé
     */
    appendSkippedTypeSelector?: boolean
}

interface Emits {
    /**
     * Émis lors de la soumission du formulaire
     */
    (e: "click:submit", value: InformationFormValue): void
    /**
     * Émis pour modifier l'opérateur
     */
    (e: "click:change-operator"): void
    /**
     * Émis lors de l'annulation
     */
    (e: "click:cancel"): void
    /**
     * Retour titulaire → sélecteur de type dans la modale d’ajout
     */
    (e: "owner-step-append-back"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    value: () => ({
        idsource: "",
        operator: "",
        brand: "",
        model: "",
        channels: [],
        name: "",
        owner: undefined,
    }),
    models: () => [],
    isEditing: false,
    hideCancel: false,
    currentOwner: null,
    appendSkippedTypeSelector: false,
})

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const { getOperator, getOperatorModels } = useSourceInfo()
const configStore = useConfigStore()

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Liste des marques disponibles pour l'opérateur
 */
const brands = ref<SourceBrand[]>([])

/**
 * Liste des modèles disponibles pour l'opérateur
 */
const operatorModels = ref<SourceModel[]>([])

/**
 * Wrapper pour le fetch des modèles et marques avec gestion du loading
 */
const { isFetching: isLoadingModelsAndBrands, fetch: fetchModelsAndBrands } =
    useFetchWrapper(async (operator: string) => {
        if (!operator) return

        const response = await getOperatorModels(operator)

        if (response) {
            brands.value = response.brands || []
            operatorModels.value = response.models || []
        }
    })

/**
 * True jusqu'à la fin du chargement initial (variables config + marques/modèles si opérateur).
 * Évite d'afficher le formulaire pendant fetchVariables alors que la progress bar ne dépend que de fetchModelsAndBrands.
 */
const isInitialSensorFormLoading = ref(true)

/**
 * Référence au composant SensorFormChannels pour accéder à isValid()
 */
const sensorFormChannelsRef = ref<InstanceType<
    typeof SensorFormChannels
> | null>(null)

/**
 * Indique si l'utilisateur a cliqué sur "Je ne trouve pas mon capteur"
 * Permet de basculer du mode "modèles suggérés" au mode "sélection manuelle"
 */
const isNoGuessed = ref<boolean>(false)

/**
 * Indique si on veut modifier le titulaire (en mode édition)
 */
const changeOwner = ref(props.isEditing ? false : true)

// ================================
// FORMULAIRE & VALIDATION
// ================================

/**
 * Schéma de validation du formulaire
 */
const schema = computed(() =>
    yup.object({
        idsource: yup.string().required(t("form.validation.idsource_required")),
        operator: yup.string().required(t("form.validation.operator_required")),
        brand: yup.string().required(t("form.validation.brand_required")),
        model: yup.string().required(t("form.validation.model_required")),
        name: yup.string().nullable(),
        channels: yup
            .array()
            .of(
                yup.object({
                    type: yup.string().nullable(),
                    disabled: yup.boolean().optional(),
                })
            )
            .default([]),
        owner: yup
            .object({
                _id_user: yup.string().optional(),
                firstname: yup.string().optional(),
                lastname: yup.string().optional(),
                username: yup.string().optional(),
                img: yup.string().optional(),
                company: yup.object().nullable().optional(),
                relation_type: yup.string().optional(),
            })
            .nullable()
            .default(null),
    })
)

const { errors, defineField, handleSubmit, setFieldValue } = useForm<{
    idsource: string
    operator: string
    brand: string
    model: string
    name: string
    channels: SourceLegacyChannelWithDisabled[]
    owner: OwnerOutput | null
}>({
    validationSchema: schema,
    initialValues: {
        idsource: props.value?.idsource || "",
        operator: props.value?.operator || "",
        brand: props.value?.brand || "",
        model: props.value?.model || "",
        name: props.value?.name || "",
        channels: props.value?.channels
            ? props.value.channels.map((ch) => ({
                  ...ch,
                  disabled: false,
              }))
            : [],
        owner: props.value?.owner || null,
    },
})

const [modelIdsource] = defineField("idsource")
const [modelOperator] = defineField("operator")
const [modelBrand] = defineField("brand")
const [modelModel] = defineField("model")
const [modelName] = defineField("name")
const [modelChannels] = defineField("channels")
const [modelOwner] = defineField("owner")

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Récupère les informations de l'opérateur sélectionné
 */
const getSelectedOperator = computed(() => {
    if (!modelOperator.value) return null
    return getOperator(modelOperator.value)
})

/**
 * Récupère la marque sélectionnée
 */
const getSelectedBrand = computed(() => {
    if (!modelBrand.value) return null
    return brands.value.find((b) => b.idname === modelBrand.value)
})

/**
 * Récupère le modèle sélectionné
 */
const getSelectedModel = computed(() => {
    if (!modelModel.value) return null
    return operatorModels.value.find((m) => m.idname === modelModel.value)
})

/**
 * Options pour les modèles suggérés (pré-détectés par la recherche automatique)
 * Retourne les modèles passés en props qui existent dans la liste des modèles de l'opérateur
 */
/**
 * Modèles suggérés résolus en SourceModel complets (cf. DEV-299).
 * `find_config` (data) envoie `{sourcebrand, model}` où `model` est l'idname.
 * Les stories/tests envoient `{idname, name}`. On accepte les 2.
 */
const getGuessedSourceModels = computed((): SourceModel[] => {
    if (!props.models?.length) return []
    const modelsToReturn: SourceModel[] = []
    props.models.forEach((m) => {
        const idnameKey =
            (m.idname as string | undefined) ?? (m.model as string | undefined)
        const nameKey = m.name as string | undefined
        const model = operatorModels.value.find(
            (model) =>
                (idnameKey && model.idname === idnameKey) ||
                (nameKey && model.name === nameKey)
        )
        if (model) modelsToReturn.push(model)
    })
    return modelsToReturn
})

/**
 * Indique si on doit afficher les modèles suggérés (Cas 1)
 * ou le formulaire complet marque/modèle (Cas 2)
 */
const getShowGuessedModels = computed((): boolean => {
    return getGuessedSourceModels.value.length > 0 && !isNoGuessed.value
})

/**
 * Marques uniques portées par les modèles suggérés.
 * DEV-299 : si plusieurs marques sont détectées par la recherche réseau,
 * l'utilisateur doit pouvoir disambiguer en changeant de marque (le select
 * marque ne doit pas être grisé dans ce cas).
 */
const getGuessedBrandIds = computed((): Set<string> => {
    const ids = new Set<string>()
    getGuessedSourceModels.value.forEach((m) => {
        if (m._id_sourcebrand) ids.add(m._id_sourcebrand)
    })
    return ids
})

/**
 * Le select marque est en lecture seule **uniquement** si la recherche a
 * détecté une seule marque (et qu'on est en mode suggéré). Dès que plusieurs
 * marques sont candidates, on libère le select pour permettre l'arbitrage
 * utilisateur (DEV-299).
 */
const getIsBrandSelectDisabled = computed((): boolean => {
    return getShowGuessedModels.value && getGuessedBrandIds.value.size <= 1
})

/**
 * Options pour le dropdown des modèles suggérés, filtrées par la marque
 * sélectionnée s'il y a une ambiguïté multi-marques (DEV-299).
 */
const getGuessedModelsOptions = computed(() => {
    const selectedBrandId = getSelectedBrand.value?._id
    return getGuessedSourceModels.value
        .filter((model) => {
            // Si plusieurs marques détectées et une marque est sélectionnée,
            // on filtre les modèles suggérés sur cette marque. Si une seule
            // marque ou pas de sélection (état transitoire), pas de filtre.
            if (getGuessedBrandIds.value.size > 1 && selectedBrandId) {
                return model._id_sourcebrand === selectedBrandId
            }
            return true
        })
        .map((model) => ({
            label: model.name,
            value: model.idname,
            img: model.img,
        }))
})

/**
 * Options pour le dropdown des marques. En mode suggéré multi-marques, on
 * restreint la liste aux seules marques détectées pour éviter de proposer
 * des choix que la recherche réseau a déjà écartés (DEV-299).
 */
const getBrandOptions = computed(() => {
    if (getShowGuessedModels.value && getGuessedBrandIds.value.size > 1) {
        return brands.value
            .filter((brand) => getGuessedBrandIds.value.has(brand._id))
            .map((brand) => ({
                label: brand.name,
                value: brand.idname,
                img: brand.img,
            }))
    }
    return brands.value.map((brand) => ({
        label: brand.name,
        value: brand.idname,
        img: brand.img,
    }))
})

/**
 * Options pour le dropdown des modèles (filtrées par marque)
 */
const getModelOptions = computed(() => {
    // Filtrer par marque sélectionnée
    return operatorModels.value
        .filter((model) => {
            // Si pas de marque sélectionnée, ne pas afficher de modèles
            if (!modelBrand.value) return false
            // Filtrer par marque (si le modèle a une référence à la marque)
            const modelWithBrand = model
            if (modelWithBrand._id_sourcebrand && getSelectedBrand.value) {
                return (
                    modelWithBrand._id_sourcebrand ===
                    getSelectedBrand.value._id
                )
            }
            return true
        })
        .map((model) => ({
            label: model.name,
            value: model.idname,
            img: model.img,
        }))
})

/**
 * Vérifie si le formulaire est valide
 */
const getIsFormValid = computed((): boolean => {
    const hasRequiredFields =
        !!modelIdsource.value &&
        !!modelOperator.value &&
        !!modelBrand.value &&
        !!modelModel.value

    // Vérifie que les channels sont valides (pas de doublons) via SensorFormChannels
    // Si le composant n'est pas encore monté ou s'il n'y a pas de channels, on considère comme valide
    const hasValidChannels =
        !getSelectedModel.value?.channels?.length ||
        !sensorFormChannelsRef.value ||
        sensorFormChannelsRef.value.isValid() === true

    // Vérifie qu'au moins un channel est activé et a un type
    const hasAtLeastOneChannel =
        modelChannels.value.length === 0 ||
        modelChannels.value.some((c) => !c.disabled && c.type && c.type !== "")

    // Si on ne veut pas modifier le titulaire, vérifie que les champs obligatoires sont remplis
    if (!changeOwner.value) {
        return hasRequiredFields && hasValidChannels && hasAtLeastOneChannel
    }

    // Vérifie que le propriétaire renseigné est valide
    const hasValidUser =
        !!modelOwner.value?.user?._id ||
        (!!modelOwner.value?.user?.firstname &&
            !!modelOwner.value?.user?.lastname &&
            !!modelOwner.value?.user?.username)

    // Vérifie que l'organisation renseignée est valide
    const hasValidCompany =
        !!modelOwner.value?.company?._id ||
        (!!modelOwner.value?.company?.name &&
            !!modelOwner.value?.company?.address &&
            !!modelOwner.value?.company?.type &&
            !!modelOwner.value?.company?.idcompany)

    const hasValidOwner = hasValidUser && hasValidCompany

    // Vérifie que le propriétaire renseigné est valide et que les champs obligatoires sont remplis
    return (
        hasRequiredFields &&
        hasValidChannels &&
        hasAtLeastOneChannel &&
        hasValidOwner
    )
})

/**
 * Options pour le toggle conserver/modifier titulaire
 */
const getOwnerModeOptions = computed(() => [
    { label: t("form.keepOwner"), value: false },
    { label: t("form.changeOwner"), value: true },
])

// ================================
// WATCHERS
// ================================

/**
 * Synchronise les valeurs initiales avec le formulaire
 */
watch(
    () => props.value,
    (newValue) => {
        if (newValue) {
            setFieldValue("idsource", newValue.idsource || "")
            setFieldValue("operator", newValue.operator || "")
            setFieldValue("brand", newValue.brand || "")
            setFieldValue("model", newValue.model || "")
            setFieldValue("name", newValue.name || "")

            if (newValue.owner) {
                setFieldValue("owner", newValue.owner)
            }

            if (newValue.channels) {
                // Convertit les channels legacy (sans disabled) vers le nouveau format
                setFieldValue(
                    "channels",
                    newValue.channels.map((ch) => ({
                        ...ch,
                        disabled: false,
                    }))
                )
            }
        }
    },
    { deep: true, immediate: true }
)

/**
 * Réinitialise le modèle quand la marque change
 *
 * DEV-299 : skip le reset si le modèle courant appartient déjà à la nouvelle
 * marque — c'est le cas quand le changement de marque est déclenché par
 * `onChangeModel` (sync auto modèle → marque). Sans ce garde, le watcher
 * efface le modèle qu'on vient de poser et l'utilisateur voit une erreur
 * rouge sur un champ qu'il a pourtant rempli.
 */
watch(modelBrand, (newBrand, oldBrand) => {
    if (newBrand === oldBrand || !oldBrand) return

    const currentModel = operatorModels.value.find(
        (m) => m.idname === modelModel.value
    )
    const newBrandObj = brands.value.find((b) => b.idname === newBrand)
    if (
        currentModel &&
        newBrandObj &&
        currentModel._id_sourcebrand === newBrandObj._id
    ) {
        // Modèle déjà cohérent avec la marque (sync via onChangeModel) — no-op
        return
    }

    setFieldValue("model", "")
    setFieldValue("channels", [])
})

/**
 * Initialise les channels quand le modèle change
 * Si le modèle a des channels, initialise avec la première variable disponible pour chaque channel
 * Réinitialise uniquement si on n'a pas déjà des channels ou si le nombre de channels ne correspond pas
 */
watch(
    () => getSelectedModel.value,
    (newModel, oldModel) => {
        if (!newModel?.channels?.length) {
            setFieldValue("channels", [])
            return
        }

        const expectedChannelCount = newModel.channels.length
        const currentChannelCount = modelChannels.value.length

        // Si le nombre de channels ne correspond pas au modèle, réinitialise
        // Ou si on n'a pas de channels du tout
        if (
            currentChannelCount === 0 ||
            currentChannelCount !== expectedChannelCount ||
            // Si le modèle a changé (pas juste une mise à jour)
            (oldModel && oldModel._id !== newModel._id)
        ) {
            // Initialise les channels avec la première variable disponible pour chaque channel
            const initializedChannels: SourceLegacyChannelWithDisabled[] =
                newModel.channels.map((channel) => {
                    const firstVariable = channel.variables?.[0] || ""
                    return {
                        type: firstVariable,
                        disabled: false,
                    }
                })

            setFieldValue("channels", initializedChannels)
        }
    }
)

/**
 * Sélectionne automatiquement le premier modèle suggéré quand les options sont disponibles
 * et qu'aucun modèle n'est encore sélectionné
 */
watch(
    () => getGuessedModelsOptions.value,
    (options) => {
        // Si on a des modèles suggérés et qu'aucun modèle n'est sélectionné
        if (options.length > 0 && !modelModel.value && !isNoGuessed.value) {
            // Sélectionne le premier modèle
            setFieldValue("model", options[0].value)
            // Met à jour la marque associée
            onChangeModel()
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Gère le changement de marque
 */
function onChangeBrand(): void {
    // Réinitialise le modèle si la marque change
    if (modelModel.value) {
        setFieldValue("model", "")
    }
}

/**
 * Gère le changement de modèle
 * Met également à jour la marque associée au modèle sélectionné
 */
function onChangeModel(): void {
    // Met à jour la marque associée au modèle sélectionné
    const selectedModel = getSelectedModel.value
    if (selectedModel?._id_sourcebrand) {
        const brand = brands.value.find(
            (b) => b._id === selectedModel._id_sourcebrand
        )
        if (brand) {
            setFieldValue("brand", brand.idname)
        }
    }
}

/**
 * Gère le clic sur "Je ne trouve pas mon capteur"
 * Bascule vers le mode sélection manuelle (marque puis modèle)
 */
function onClickNoGuessed(): void {
    isNoGuessed.value = true
    // Réinitialise la marque et le modèle sélectionné
    setFieldValue("brand", "")
    setFieldValue("model", "")
    setFieldValue("channels", [])
}

/**
 * Gère la mise à jour des channels depuis SensorFormChannels
 */
function onChannelsUpdate(channels: SourceLegacyChannelWithDisabled[]): void {
    setFieldValue("channels", channels)
}

/**
 * Gère la soumission du formulaire
 */
const onSubmit = handleSubmit(() => {
    const formValue: InformationFormValue = {
        idsource: modelIdsource.value,
        operator: modelOperator.value,
        brand: modelBrand.value,
        model: modelModel.value,
        channels: modelChannels.value,
        name: modelName.value,
    }

    // Si on veut modifier le titulaire, ajoute le propriétaire au formulaire
    if (changeOwner.value) {
        formValue.owner = modelOwner.value
    }

    emit("click:submit", formValue)
})

/**
 * Gère la mise à jour du propriétaire depuis SourceFormOwner
 */
function onUpdateOwner(ownerOutput: OwnerOutput): void {
    setFieldValue("owner", ownerOutput)
}

/**
 * Gère le changement de mode (conserver/modifier titulaire)
 * @param value - Nouvelle valeur du mode
 */
function onChangeOwnerMode(value: boolean): void {
    changeOwner.value = value
}

/**
 * Gère le clic sur le bouton de modification de l'opérateur
 */
function onClickChangeOperator(): void {
    emit("click:change-operator")
}

/**
 * Gère le clic sur le bouton d'annulation
 */
function onClickCancel(): void {
    emit("click:cancel")
}

// ================================
// LIFECYCLE
// ================================

/**
 * Charge les modèles, marques et variables au montage
 */
onBeforeMount(async () => {
    try {
        // Charge les variables pour pouvoir afficher leurs noms lisibles
        await configStore.fetchVariables()

        if (props.value?.operator) {
            await fetchModelsAndBrands(props.value.operator)
        }

        if (props.value?.channels) {
            // Convertit les channels legacy (sans disabled) vers le nouveau format
            setFieldValue(
                "channels",
                props.value.channels.map((ch) => ({
                    ...ch,
                    disabled: false,
                }))
            )
        }
    } finally {
        isInitialSensorFormLoading.value = false
    }
})
</script>

<template>
    <div class="flex flex-col gap-6">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <h1 class="text-xl font-semibold text-gray-700 text-center">
            {{ t("form.title") }}
        </h1>

        <!-- ================================ -->
        <!-- CHARGEMENT -->
        <!-- ================================ -->
        <BaseProgressBar
            mode="indeterminate"
            v-if="isInitialSensorFormLoading || isLoadingModelsAndBrands"
        />

        <!-- ================================ -->
        <!-- FORMULAIRE -->
        <!-- ================================ -->
        <form v-else class="flex flex-col gap-6" @submit.prevent="onSubmit">
            <!-- ================================ -->
            <!-- INFORMATIONS LECTURE SEULE -->
            <!-- ================================ -->
            <div class="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
                <!-- Opérateur -->
                <div class="flex items-center gap-4">
                    <span
                        class="text-sm font-medium text-gray-600 min-w-[120px]"
                    >
                        {{ t("form.operator_label") }} :
                    </span>
                    <div class="flex items-center gap-2">
                        <img
                            v-if="getSelectedOperator?.img"
                            :src="getSelectedOperator.img"
                            :alt="getSelectedOperator.name"
                            class="w-6 h-6 object-contain"
                        />
                        <span class="text-sm text-gray-800 font-semibold">
                            {{ getSelectedOperator?.name || modelOperator }}
                        </span>
                    </div>
                </div>

                <!-- ID Source -->
                <div class="flex items-center gap-4">
                    <span
                        class="text-sm font-medium text-gray-600 min-w-[120px]"
                    >
                        {{ t("form.idsource_label") }} :
                    </span>
                    <span class="text-sm text-gray-800 font-mono">
                        {{ modelIdsource }}
                    </span>
                </div>

                <!-- Bouton modifier opérateur -->
                <BaseButton
                    type="button"
                    :label="t('form.change_operator')"
                    severity="secondary"
                    size="small"
                    class="w-fit"
                    @click="onClickChangeOperator"
                />
            </div>

            <!-- ================================ -->
            <!-- SÉLECTION MARQUE -->
            <!-- ================================ -->
            <fieldset class="flex flex-col gap-4 border-none p-0">
                <legend class="text-lg font-semibold text-gray-700 mb-2">
                    {{ t("form.brand_label") }}
                </legend>
                <div class="flex items-start gap-4">
                    <!-- Image de la marque -->
                    <div
                        class="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
                    >
                        <img
                            v-if="getSelectedBrand?.img"
                            :src="getSelectedBrand.img"
                            :alt="getSelectedBrand.name"
                            class="w-16 h-16 object-contain"
                        />
                    </div>
                    <!-- Sélecteur + message si auto-détecté -->
                    <div class="flex-1 flex flex-col gap-2">
                        <BaseInputWrapper
                            input-id="sensor-brand"
                            :label="t('form.brand_label')"
                            :error="errors.brand"
                        >
                            <BaseSelect
                                v-model="modelBrand"
                                input-id="sensor-brand"
                                :options="getBrandOptions"
                                option-label="label"
                                option-value="value"
                                :disabled="getIsBrandSelectDisabled"
                                :invalid="!!errors.brand"
                                :loading="isLoadingModelsAndBrands"
                                class="w-full"
                                @change="onChangeBrand"
                            >
                                <template #value="slotProps">
                                    <div
                                        v-if="slotProps.value"
                                        class="flex items-center gap-2"
                                    >
                                        <img
                                            v-if="getSelectedBrand?.img"
                                            :src="getSelectedBrand.img"
                                            :alt="getSelectedBrand.name"
                                            class="w-6 h-6 object-contain"
                                        />
                                        <span>
                                            {{ getSelectedBrand?.name }}
                                        </span>
                                    </div>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center gap-2">
                                        <img
                                            v-if="slotProps.option?.img"
                                            :src="slotProps.option.img"
                                            :alt="slotProps.option.label"
                                            class="w-6 h-6 object-contain"
                                        />
                                        <span>
                                            {{ slotProps.option.label }}
                                        </span>
                                    </div>
                                </template>
                            </BaseSelect>
                        </BaseInputWrapper>
                        <!-- Message si marque auto-détectée -->
                        <p
                            v-if="getShowGuessedModels"
                            class="text-sm text-gray-500 italic"
                        >
                            {{ t("form.brand_auto_detected") }}
                        </p>
                    </div>
                </div>
            </fieldset>

            <!-- ================================ -->
            <!-- SÉLECTION MODÈLE -->
            <!-- ================================ -->
            <fieldset
                v-if="modelBrand"
                class="flex flex-col gap-4 border-none p-0"
            >
                <legend class="text-lg font-semibold text-gray-700 mb-2">
                    {{ t("form.model_label") }}
                </legend>
                <div class="flex items-start gap-4">
                    <!-- Image du modèle -->
                    <div
                        class="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
                    >
                        <img
                            v-if="getSelectedModel?.img"
                            :src="getSelectedModel.img"
                            :alt="getSelectedModel?.name"
                            class="w-16 h-16 object-contain"
                        />
                    </div>
                    <!-- Sélecteur -->
                    <BaseInputWrapper
                        input-id="sensor-model"
                        :label="t('form.model_label')"
                        :error="errors.model"
                        class="flex-1"
                    >
                        <BaseSelect
                            v-model="modelModel"
                            input-id="sensor-model"
                            :options="
                                getShowGuessedModels
                                    ? getGuessedModelsOptions
                                    : getModelOptions
                            "
                            option-label="label"
                            option-value="value"
                            :invalid="!!errors.model"
                            :loading="isLoadingModelsAndBrands"
                            class="w-full"
                            @change="onChangeModel"
                        >
                            <template #value="slotProps">
                                <div
                                    v-if="slotProps.value"
                                    class="flex items-center gap-2"
                                >
                                    <img
                                        v-if="getSelectedModel?.img"
                                        :src="getSelectedModel.img"
                                        :alt="getSelectedModel?.name"
                                        class="w-6 h-6 object-contain"
                                    />
                                    <span>{{ getSelectedModel?.name }}</span>
                                </div>
                            </template>
                            <template #option="slotProps">
                                <div class="flex items-center gap-2">
                                    <img
                                        v-if="slotProps.option?.img"
                                        :src="slotProps.option.img"
                                        :alt="slotProps.option.label"
                                        class="w-6 h-6 object-contain"
                                    />
                                    <span>{{ slotProps.option.label }}</span>
                                </div>
                            </template>
                        </BaseSelect>
                    </BaseInputWrapper>
                </div>
                <!-- Bouton "Je ne trouve pas mon capteur" (uniquement si modèles suggérés) -->
                <BaseButton
                    v-if="getShowGuessedModels"
                    type="button"
                    :label="t('form.not_found_model')"
                    variant="text"
                    class="w-fit mx-auto"
                    @click="onClickNoGuessed"
                />
            </fieldset>

            <!-- ================================ -->
            <!-- SECTIONS APRÈS SÉLECTION DU MODÈLE -->
            <!-- ================================ -->
            <template v-if="modelModel">
                <!-- ================================ -->
                <!-- CANAUX -->
                <!-- ================================ -->
                <fieldset
                    v-if="getSelectedModel?.channels?.length"
                    class="flex flex-col gap-4 border-none p-0"
                >
                    <legend class="text-lg font-semibold text-gray-700 mb-2">
                        {{ t("form.channels_label") }}
                    </legend>
                    <SensorFormChannels
                        ref="sensorFormChannelsRef"
                        :operator-id="modelOperator"
                        :model-id="modelModel"
                        :model-value="modelChannels"
                        @update:model-value="onChannelsUpdate"
                    />
                </fieldset>

                <!-- ================================ -->
                <!-- NOM DU CAPTEUR -->
                <!-- ================================ -->
                <fieldset class="flex flex-col gap-4 border-none p-0">
                    <legend class="text-lg font-semibold text-gray-700 mb-2">
                        {{ t("form.name_label") }}
                        <span class="text-sm font-normal text-gray-500 ml-2"
                            >(optionnel)</span
                        >
                    </legend>
                    <BaseInputWrapper
                        input-id="sensor-name"
                        :label="t('form.name_label')"
                        :hint="t('form.name_hint')"
                        :error="errors.name"
                    >
                        <BaseInputText
                            v-model="modelName"
                            input-id="sensor-name"
                            :invalid="!!errors.name"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <!-- ================================ -->
                <!-- PROPRIÉTAIRE -->
                <!-- ================================ -->
                <fieldset class="flex flex-col gap-4 border-none p-0">
                    <legend class="text-lg font-semibold text-gray-700 mb-2">
                        {{ t("form.owner_label") }}
                    </legend>

                    <!-- Toggle Conserver/Modifier (mode édition) -->
                    <BaseSelectButton
                        v-if="isEditing"
                        :model-value="changeOwner"
                        :options="getOwnerModeOptions"
                        option-label="label"
                        option-value="value"
                        :allow-empty="false"
                        @update:model-value="onChangeOwnerMode"
                        class="mx-auto"
                    />

                    <!-- Affichage du titulaire actuel (mode édition, sans modification) -->
                    <div
                        v-if="!changeOwner && currentOwner"
                        class="p-4 bg-gray-50 rounded-lg"
                    >
                        <h5 class="text-sm font-medium text-gray-600 mb-3">
                            {{ t("form.currentOwner") }}
                        </h5>
                        <ItemCompany
                            v-if="currentOwner.relation_type === 'company'"
                            :company="currentOwner"
                        />
                        <BaseMessage v-else severity="warning">
                            <p class="text-sm">
                                {{ t("form.currentOwnerWarning") }}
                            </p>
                        </BaseMessage>
                    </div>

                    <!-- Formulaire de sélection du titulaire -->
                    <SourceFormOwner
                        v-if="changeOwner"
                        :model-value="modelOwner"
                        @update:model-value="onUpdateOwner"
                        allowed-user-type="professional"
                        :warning-particulier-not-allowed="
                            t('form.warningParticulierNotAllowed')
                        "
                    >
                    </SourceFormOwner>
                </fieldset>
            </template>

            <!-- ================================ -->
            <!-- ACTIONS -->
            <!-- ================================ -->
            <div class="flex flex-col sm:flex-row gap-4 mt-4">
                <BaseButton
                    type="submit"
                    :label="t('form.submit')"
                    severity="primary"
                    :disabled="!getIsFormValid"
                    class="flex-1"
                />
                <BaseButton
                    v-if="!hideCancel"
                    type="button"
                    :label="t('form.cancel')"
                    severity="secondary"
                    class="flex-1"
                    @click="onClickCancel"
                />
            </div>
        </form>
    </div>
</template>
